/**
 * Authentication context, hooks, and route guards.
 *
 * The guards here decide what to *render*. They are not the authorisation boundary: the
 * API is, and it enforces its own rules on every route. A hidden navigation item is not
 * access control, and this file must never be the only thing standing between a user and
 * data they should not see.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { runtimeConfig } from '../config/runtime'
import { ApiClient } from '../api/client'
import {
  DevelopmentAuthenticator,
  ROLES,
  ROLE_LABELS,
  buildAuthenticator,
  hasRole,
  type AuthStatus,
  type Authenticator,
  type Identity,
  type Role,
} from './identity'

interface AuthContextValue {
  readonly status: AuthStatus
  readonly identity: Identity | undefined
  readonly mode: Authenticator['mode']
  readonly error: string | undefined
  readonly api: ApiClient
  signIn(): Promise<void>
  signOut(): Promise<void>
  /** Local only: adopt a role. Undefined when enterprise sign-in is in use. */
  readonly assumeRole:
    ((roles: readonly Role[], workerId?: string) => void) | undefined
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const config = useMemo(() => runtimeConfig(), [])
  const authenticator = useMemo(() => buildAuthenticator(config), [config])

  const [status, setStatus] = useState<AuthStatus>('initialising')
  const [identity, setIdentity] = useState<Identity | undefined>(undefined)
  const [error, setError] = useState<string | undefined>(undefined)

  // One client for the application's lifetime, holding a reference to the authenticator
  // rather than a token. A captured token would be stale the moment it was renewed.
  const api = useMemo(
    () =>
      new ApiClient({
        basePath: config.apiBasePath,
        credential: () => authenticator.credential(),
      }),
    [authenticator, config.apiBasePath],
  )

  useEffect(() => {
    let cancelled = false
    authenticator
      .initialise()
      .then((resolved) => {
        if (cancelled) return
        setIdentity(resolved)
        setStatus(resolved === undefined ? 'anonymous' : 'authenticated')
      })
      .catch((cause: unknown) => {
        if (cancelled) return
        setError(
          cause instanceof Error
            ? cause.message
            : 'Sign-in could not be completed.',
        )
        setStatus('error')
      })
    return () => {
      cancelled = true
    }
  }, [authenticator])

  const signIn = useCallback(async () => {
    try {
      const resolved = await authenticator.signIn()
      // Entra `loginRedirect` navigates away and resolves undefined. Treating that as
      // anonymous puts the sign-in screen back on top of a redirect that is still in
      // flight — which is the loop after Entra returns to this origin.
      if (resolved === undefined && authenticator.mode === 'entra') {
        return
      }
      setIdentity(resolved)
      setStatus(resolved === undefined ? 'anonymous' : 'authenticated')
    } catch (cause: unknown) {
      setError(
        cause instanceof Error
          ? cause.message
          : 'Sign-in could not be completed.',
      )
      setStatus('error')
    }
  }, [authenticator])

  const signOut = useCallback(async () => {
    await authenticator.signOut()
    setIdentity(undefined)
    setStatus('anonymous')
  }, [authenticator])

  const assumeRole = useMemo(() => {
    if (!(authenticator instanceof DevelopmentAuthenticator)) return undefined
    return (roles: readonly Role[], workerId?: string) => {
      setIdentity(authenticator.assume(roles, workerId))
      setStatus('authenticated')
    }
  }, [authenticator])

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      identity,
      mode: authenticator.mode,
      error,
      api,
      signIn,
      signOut,
      assumeRole,
    }),
    [
      status,
      identity,
      authenticator.mode,
      error,
      api,
      signIn,
      signOut,
      assumeRole,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext)
  if (value === undefined) {
    throw new Error('useAuth must be used inside an AuthProvider.')
  }
  return value
}

export function useIdentity(): Identity | undefined {
  return useAuth().identity
}

export function useApi(): ApiClient {
  return useAuth().api
}

export function useHasRole(...roles: readonly Role[]): boolean {
  return hasRole(useAuth().identity, ...roles)
}

export { ROLES, ROLE_LABELS, hasRole }
export type { Identity, Role }
